import Cookies from 'js-cookie'
import { config, getApiUrl } from './config'

declare global {
  interface Window {
    _sift?: Array<[string, ...unknown[]]>
  }
}

const SIFT_SCRIPT_ID = 'sjs'
const SIFT_SCRIPT_SRC = 'https://sic.pixai.art/s.js'
const BSID_COOKIE_NAME = '_bsid'

let initialized = false
let scriptLoaded = false
let lastSessionId: string | null = null
let lastUserId: string | undefined
let lastSiftBeaconKey: string | undefined

const ensureSift = () => {
  window._sift = window._sift || []
  return window._sift
}

const isProdSiftEnv = () => {
  if (typeof window === 'undefined') return false
  const apiUrl = getApiUrl()
  const hostname = window.location.hostname
  const isProdApi = apiUrl.includes('api.pixai.art')
  const isProdHost = hostname === 'pixai.art' || hostname.endsWith('.pixai.art')
  return isProdApi && isProdHost
}

const getSiftJsKey = () =>
  isProdSiftEnv() ? config.siftJsKeyProd : config.siftJsKeySandbox

const shouldEnableSift = (pathname?: string) => {
  if (!getSiftJsKey()) return false
  if (typeof window === 'undefined') return false
  const path = pathname ?? window.location.pathname
  return !path.startsWith('/embedded/')
}

const loadSiftScript = () => {
  if (scriptLoaded || typeof document === 'undefined') return
  const existing = document.getElementById(SIFT_SCRIPT_ID)
  if (existing) {
    scriptLoaded = true
    return
  }

  const script = document.createElement('script')
  script.id = SIFT_SCRIPT_ID
  script.async = true
  script.src = SIFT_SCRIPT_SRC
  document.body.appendChild(script)
  scriptLoaded = true
}

const getBsid = () => {
  if (typeof window === 'undefined') return null
  return Cookies.get(BSID_COOKIE_NAME) ?? null
}

export const initSift = (options?: { userId?: string; pathname?: string }) => {
  if (!shouldEnableSift(options?.pathname)) return false
  const siftBeaconKey = getSiftJsKey()
  const sessionId = getBsid()
  // Currently, don't report anything when no server set bsid is present
  if (!sessionId) return false
  const sift = ensureSift()

  const shouldResetAccount = siftBeaconKey !== lastSiftBeaconKey
  if (!initialized || shouldResetAccount) {
    sift.push(['_setAccount', siftBeaconKey])
    if (shouldResetAccount && typeof document !== 'undefined') {
      const existing = document.getElementById(SIFT_SCRIPT_ID)
      if (existing) existing.remove()
      scriptLoaded = false
    }
    loadSiftScript()
    initialized = true
    lastSiftBeaconKey = siftBeaconKey
  }

  if (sessionId !== lastSessionId) {
    sift.push(['_setSessionId', sessionId])
    lastSessionId = sessionId
  }

  if (options?.userId && options.userId !== lastUserId) {
    sift.push(['_setUserId', options.userId])
    lastUserId = options.userId
  }

  return true
}

export const setSiftUserId = (userId?: string) => {
  if (!shouldEnableSift()) return
  if (!getBsid()) return
  if (userId === lastUserId) return
  const sift = ensureSift()
  sift.push(['_setUserId', userId ?? ''])
  lastUserId = userId
}

export const trackSiftPageView = (pathname?: string) => {
  if (!initSift({ pathname })) return
  ensureSift().push(['_trackPageview'])
}
